/**
 * Catálogo COMPARTIDO de fotos de ingredientes.
 *
 * Por qué compartido y no una foto por opción: en prod `carne mechada` aparece
 * 40 veces en 9 locales y `pollo` 45 veces en 12. Una foto pegada a la fila de
 * opción obligaría al dueño a subir la misma carne mechada hasta 8 veces. Y 18
 * de 77 locales subieron alguna foto de plato en meses: un diseño que empiece
 * en "el dueño sube fotos" no llega a producción.
 *
 * Las fotos son DEL INGREDIENTE (primer plano), no de un plato terminado: le
 * prometen al cliente el ingrediente, no la presentación del local.
 *
 * Agregar una foto: subirla a `menu-images/catalog/<slug>.webp`, agregar el
 * slug a CATALOG_SLUGS y sus nombres a CATALOG_NAMES — salvo que el nombre
 * sea un sustantivo de proteína a secas cuya forma cambia según el plato
 * (`pollo`, `carne`, `res`), en cuyo caso va a AMBIGUOUS_NAMES en vez de
 * CATALOG_NAMES (ver el comentario ahí). Sin migración.
 */

/** Un slug = un archivo `<slug>.webp` en menu-images/catalog/. */
export const CATALOG_SLUGS = [
  'carne-mechada',
  'pollo-mechado',
  'pernil',
  'queso-frito',
  'pollo',
  'pollo-filete',
  'jamon',
  'maiz',
  'cebolla',
  'tomate',
  'papas-fritas',
  'pina',
  'champinones',
  'pepperoni',
  'chorizo',
  // Lote 3 (2026-09-04). Rellenos y toppings de empanada: nacen de Empanada
  // Factory, pero `vibra` comparte 5 de estos nombres EXACTOS y otros seis
  // locales comparten el nombre corto, así que no son de un solo local.
  'cazon',
  'mariscos',
  'queso-clasico',
  'la-jamonada',
  'ensalada-rayada',
  'caraotas',
  'pico-de-gallo',
  'platano-maduro',
  'queso-gouda',
  'queso-llanero',
  'salchichas-salsa-rosada',
  // Lote 2 (2026-09-02). Elegidos por alcance MEDIDO en prod, no por gusto:
  // son los seis nombres sin foto que más locales distintos usan.
  'tocineta',
  'carne-hamburguesa',
  'chuleta-ahumada',
  'lomito',
  'huevo',
  'aguacate',
] as const

export type CatalogSlug = (typeof CATALOG_SLUGS)[number]

/**
 * Nombre normalizado (canónico o alias) → slug.
 *
 * Las claves ya vienen normalizadas: minúsculas, sin tildes, espacios simples.
 * `ingredientCatalog.test.ts` lo verifica, así que una clave mal escrita
 * (`"Carne Mechada"`, `"jamón"`) rompe el build de tests, no la producción.
 *
 * NINGUNA clave puede empezar con una negación (`sin `, `no `, …): eso pondría
 * una foto apetitosa junto a "Sin queso". El test lo impide.
 */
export const CATALOG_NAMES: Readonly<Record<string, CatalogSlug>> = {
  // Proteínas. Los alias salen de nombres REALES de prod, no de variantes
  // imaginadas: 'res mechada' (8 usos) y 'carne desmechada' (3) son la misma
  // carne. `carne` a secas queda FUERA a propósito — en varios menús es la
  // carne de hamburguesa, no la mechada.
  'carne mechada': 'carne-mechada',
  'res mechada': 'carne-mechada',
  'carne desmechada': 'carne-mechada',
  'carne esmechada': 'carne-mechada',
  'pollo mechado': 'pollo-mechado',
  'pollo esmechado': 'pollo-mechado',
  'pollo desmechado': 'pollo-mechado',
  pernil: 'pernil',
  // 'pollo' a secas queda FUERA de este mapa a propósito — ver AMBIGUOUS_NAMES
  // más abajo. 'pollo a la plancha' y 'pollo al grill' sí son preparaciones
  // inequívocas (siempre tiras a la plancha, en cualquier plato) y resuelven
  // directo igual que antes; quedan fuera 'pollo crispy' y 'pollo empanizado',
  // que son empanizados y se ven distintos, y 'pollo asado', que es otra cosa.
  'pollo a la plancha': 'pollo',
  'pollo al grill': 'pollo',

  // Quesos y fiambres. 'jamon y queso' queda fuera: son dos ingredientes.
  'queso frito': 'queso-frito',
  jamon: 'jamon',
  chorizo: 'chorizo',
  pepperoni: 'pepperoni',

  // Vegetales y acompañantes. Las tildes no van en la clave: normalizeLabel
  // las quita, y el test de integridad exige que la clave ya esté normalizada
  // ('piña' entra como 'pina', 'maíz' como 'maiz').
  maiz: 'maiz',
  cebolla: 'cebolla',
  tomate: 'tomate',
  'papas fritas': 'papas-fritas',
  'papa frita': 'papas-fritas',
  pina: 'pina',
  champinones: 'champinones',
  champinon: 'champinones',

  // --- Lote 2 ---
  // Sigue mandando la misma regla: entra el nombre que describe una
  // PREPARACIÓN (una sola forma física), no el sustantivo de proteína a secas.
  // Por eso 'carne de hamburguesa' entra y 'hamburguesa' a secas NO: en los
  // grupos de proteína de un local es la carne, pero en otro menú es el
  // sándwich entero.
  'carne de hamburguesa': 'carne-hamburguesa',
  'extra carne de hamburguesa': 'carne-hamburguesa',
  'carne de hamburguesa adicional': 'carne-hamburguesa',
  // 'chuleta' a secas son los mismos 17 usos de la ahumada: se revisaron los
  // grupos uno por uno y en prod no hay ninguna otra chuleta.
  'chuleta ahumada': 'chuleta-ahumada',
  chuleta: 'chuleta-ahumada',
  'extra chuleta': 'chuleta-ahumada',
  // La foto es lomito de RES a la parrilla, en tiras. 'extra lomito de pollo'
  // queda fuera a propósito: mismo sustantivo, otra carne, otra foto.
  lomito: 'lomito',
  'extra lomito': 'lomito',
  'extra de lomito': 'lomito',
  'extra lomito de res': 'lomito',

  // Tocineta. Los tres nombres conviven en prod ('tocineta' 6 locales,
  // 'tocino' 1, 'bacon' 2) y son el mismo ingrediente. 'tocineta con queso'
  // queda fuera: son dos ingredientes, igual que 'jamon y queso'.
  tocineta: 'tocineta',
  tocino: 'tocineta',
  bacon: 'tocineta',
  'extra tocineta': 'tocineta',
  'extra de tocineta': 'tocineta',
  'tocineta extra': 'tocineta',
  'tocino extra': 'tocineta',
  'extra bacon': 'tocineta',
  'extra bacon crujiente': 'tocineta',

  // Huevo FRITO. 'huevo cocido' queda fuera aunque exista en prod ('extra
  // huevo cocido', 'sin huevo cocido'): es otra forma física.
  huevo: 'huevo',
  'extra de huevo': 'huevo',
  'extra huevo frito': 'huevo',

  // Aguacate en tajadas. El nombre bilingüe entra tal cual porque el match es
  // EXACTO: sin esa clave, Empanada Factory se queda sin la foto. Y
  // 'extra salsa avocado' queda fuera — es una salsa, no la fruta.
  aguacate: 'aguacate',
  'aguacate (avocado)': 'aguacate',

  // --- Formas "extra …" de slugs que ya existían ---
  // El match es EXACTO a propósito, así que "Extra Pernil" no hereda nada de
  // `pernil`: necesita su propia clave. Estas 11 son las formas REALES que hay
  // en prod, contadas una por una — no se generó ninguna variante imaginada, y
  // sobre todo NO se aflojó el matcher (nada de recortar prefijos en tiempo de
  // ejecución, que es lo que produciría falsos positivos como
  // "extra lomito de pollo" -> lomito de res).
  //
  // 'extra pollo' y 'pollo extra' NO están acá: heredan la ambigüedad de
  // `pollo` y viven en AMBIGUOUS_NAMES, más abajo.
  'extra carne mechada': 'carne-mechada',
  'carne desmechada adicional': 'carne-mechada',
  'extra pollo mechado': 'pollo-mechado',
  'pollo desmechado adicional': 'pollo-mechado',
  'extra pernil': 'pernil',
  'pernil extra': 'pernil',
  'extra queso frito': 'queso-frito',
  'extra de queso frito': 'queso-frito',
  'extra jamon': 'jamon',
  'extra maiz': 'maiz',
  'extra tomate': 'tomate',

  // Toppings de Empanada Factory renombrados para no confundirse con la
  // empanada del mismo nombre («Carne Mechada» → «Extra de carne mechada»).
  // Misma foto que el nombre corto. 'extra de reina pepiada' NO entra: la
  // reina pepiada no tiene foto en el catálogo.
  'extra de carne mechada': 'carne-mechada',
  'extra de pollo esmechado': 'pollo-mechado',
  'extra de cazon': 'cazon',
  'extra de mariscos': 'mariscos',
  'extra de chorizo': 'chorizo',

  // --- Lote 3: rellenos y toppings de empanada ---
  // Los nombres bilingües entran con paréntesis y todo porque el match es
  // EXACTO: son las cadenas literales que Empanada Factory y `vibra` tienen
  // en prod. El nombre corto va aparte cuando OTRO local lo usa de verdad.
  cazon: 'cazon',
  'cazon (shredded baby shark)': 'cazon',
  mariscos: 'mariscos',
  'seafood mix (premium mariscos)': 'mariscos',
  'pico de gallo': 'pico-de-gallo',
  'ensalada rayada': 'ensalada-rayada',
  'salchichas en salsa rosada': 'salchichas-salsa-rosada',

  // Caraotas negras. `frijoles` a secas queda FUERA: Arepísimas tiene además
  // 'Frijoles Rosas', así que la palabra sola no dice de qué color son.
  'frijoles negros (caraotas)': 'caraotas',
  'frijoles negros': 'caraotas',

  // Plátano MADURO (dulce, frito, dorado). El verde es otra foto y otro
  // ingrediente: 'platano verde', 'platano frito' y 'patacon' NO entran acá.
  'platanos (sweet plantains)': 'platano-maduro',
  'platano maduro': 'platano-maduro',
  'platano amarillo': 'platano-maduro',

  // Tres quesos que a 64px hay que poder distinguir, así que cada foto tiene
  // una FORMA distinta: gouda en hebras amarillas, llanero en hebras blancas,
  // clásico en tacos blancos.
  'queso gouda amarillo (melted gouda)': 'queso-gouda',
  'queso llanero (traditional shredded white cheese)': 'queso-llanero',
  'queso llanero': 'queso-llanero',
  'queso clasico': 'queso-clasico',

  // Relleno de la casa con receta conocida: la descripción del propio ítem en
  // prod dice "Signature mix: Diced ham, white cheese, gouda", así que la foto
  // muestra esos tres y nada más. No es el caso de 'jamon y queso', que sigue
  // fuera: ahí no hay receta, son dos ingredientes sueltos.
  'la jamonada': 'la-jamonada',
}

/**
 * Nombres AMBIGUOS: la misma cadena describe formas físicas distintas según
 * el plato. Este es el otro lado de la clasificación que ya dividía el
 * catálogo — `carne` y `res` a secas ya estaban fuera de CATALOG_NAMES por
 * esta misma razón, sin estructura propia porque no había una segunda foto
 * con la que desambiguar. `pollo` fue el primer caso con las tres fotos
 * disponibles, así que se le da la estructura.
 *
 * LA REGLA GENERAL, para quien agregue el próximo nombre:
 *
 *   - Una PREPARACIÓN (`carne mechada`, `queso frito`, `pollo mechado`,
 *     `pernil`) tiene una sola forma física sin importar el plato en el que
 *     aparezca — va en CATALOG_NAMES, match directo.
 *   - Un SUSTANTIVO DE PROTEÍNA A SECAS (`pollo`, `carne`, `res`) cambia de
 *     forma según el plato — filete en la hamburguesa, esmechado en la
 *     empanada, topping en la pizza — y matchearlo directo produce falsos
 *     positivos: 17 de 48 apariciones reales de `pollo` en contexto de
 *     empanada/arepa mostraban una foto de filete a la plancha que el
 *     cliente no iba a recibir. Va acá, con una regla por contexto.
 *
 * Cada regla prueba una regex (case-insensitive) contra el nombre CRUDO del
 * GRUPO al que pertenece la opción (`item_modifier_groups.modifier_groups
 * .name`, el interno — nunca el `display_name` que ve el cliente: incluye
 * "Elige tu Proteína" para grupos genéricos y ahí es donde SIN contexto
 * resuelve `null`). Se evalúan en orden y gana la primera que matchea. Sin
 * nombre de grupo, o si ninguna regla matchea, el resultado es `null` — igual
 * que un nombre que no está en ningún lado del catálogo. Nunca se adivina.
 */
export type AmbiguousContextRule = {
  /** Probada contra el nombre del grupo, sin normalizar (case-insensitive). */
  pattern: RegExp
  slug: CatalogSlug
}

const REGLAS_POLLO: readonly AmbiguousContextRule[] = [
  // Filete de hamburguesa/perro: foto NUEVA y distinta de 'pollo' — el
  // corte de pechuga de una hamburguesa no se parece a las tiras a la
  // plancha de 'pollo a la plancha'/'pollo al grill' (CATALOG_NAMES).
  { pattern: /hamburgues|burger|perro|hot ?dog/i, slug: 'pollo-filete' },
  // Esmechado: mismo slug que 'pollo mechado' en CATALOG_NAMES.
  { pattern: /empanada|pastelito|arepa|cachapa|patac/i, slug: 'pollo-mechado' },
  // Topping de pizza: reusa la foto YA EXISTENTE de tiras a la plancha —
  // mismo slug que 'pollo a la plancha'/'pollo al grill' en CATALOG_NAMES.
  { pattern: /pizza/i, slug: 'pollo' },
]

export const AMBIGUOUS_NAMES: Readonly<Record<string, readonly AmbiguousContextRule[]>> = {
  pollo: REGLAS_POLLO,
  // 'Extra Pollo' es tan ambiguo como 'Pollo': la palabra "extra" no dice nada
  // de la forma física. Comparten el MISMO array a propósito — dos copias se
  // desincronizarían y 'Pollo' y 'Extra Pollo' mostrarían fotos distintas en
  // el mismo grupo. En prod son 5 usos en 5 locales.
  'extra pollo': REGLAS_POLLO,
  'pollo extra': REGLAS_POLLO,
}

/**
 * Prefijos de CANTIDAD PARCIAL. Complemento de `isRemovalLabel`
 * (lib/menu/classifyModifier.ts), que es la definición compartida de remoción
 * y NO se toca desde acá: modificarla cambiaría cómo el flujo guiado reparte
 * opciones entre Agregar y Quitar.
 *
 * "POCA SALSA" / "POCO QUESO" existen en prod y no son remociones — pero
 * tampoco merecen una foto apetitosa del ingrediente entero.
 */
export const PARTIAL_PREFIXES = ['poco ', 'poca ', 'medio ', 'media '] as const

/** Ruta del objeto dentro del bucket público `menu-images`. */
export function catalogObjectPath(slug: CatalogSlug): string {
  return `catalog/${slug}.webp`
}
